import * as THREE from 'three';
import { v4 as uuid } from 'uuid';
import type { Viewer } from '../../../viewer/viewer';
import type { IViewConfig } from '../../types';
import { ViewModes } from '../../helpers';
import { OpeningKind, DoorType } from 'shared-types';
import {
  getOpeningRoomIdsProperty,
  getOpeningSketchWallEdgeIdProperty,
  getOpeningSketchOffsetProperty,
  getOpeningSketchLengthProperty,
  getOpeningSketchLowerBoundProperty,
  getOpeningSketchHeightProperty,
} from '@pointorama/potree-shared';
import { OpeningAnnotation } from '../../../utils/PointoramaAnnotation';
import { pointInPolygonXY } from '../../../utils/resolveRoomForPoint';
import { PointoramaRoom } from '../../../utils/PointoramaRoom';
import { PointoramaWallSketch } from '../../../utils/PointoramaWallSketch';
import { openingSketchParamsFromPolygon, OpeningSketchGeometryParams, OpeningSketchRequest } from '../../../utils/skeleton/replicateOpeningSketches';
import { OpeningSketchGeometryValues, readOpeningSketchGeometry, writeOpeningSketchGeometry, defaultOpeningInset } from '../modules/openingSketch/OpeningSketchShared';
import { EditableOpeningSketchRect, OpeningSketchElevationOverlay, pickOpeningSketchRectAtPixel } from '../modules/openingSketch/OpeningSketchElevation';
import type { AnnotationKindAdapter, CommitContext, HitTestContext } from './line-tool/AnnotationKindAdapter';
import type { EditableAnnotation } from '../modules/MeasureEngine';
import type { EditableShape } from './line-tool/EditableShape';
import type { IPersona } from '../../types';
import type { Viewer as ViewerType } from '../../../viewer/viewer';
import type { BasePlaneState, SelectionContextProvider } from './line-tool/SelectionContextProvider';
import { FloorSelectionContext } from './line-tool/FloorSelectionContext';

/**
 * Max total along-wall adjustment (slide + shrink) used to auto-resolve a new
 * opening that slightly overlaps an existing one on the same edge. Overlaps
 * needing more than this are still rejected with the overlap toast — a draw
 * landing mostly on top of an existing opening is a mistake, not inaccuracy.
 */
const MAX_OPENING_OVERLAP_ADJUST_M = 0.3;

/** Never auto-shrink an opening below this length — reject instead. */
const MIN_ADJUSTED_OPENING_LENGTH_M = 0.3;

/**
 * Kind adapter for the LineTool nested inside `DoorWindowTool` for
 * phase-2 (wall focus). The outer DoorWindowTool owns phase-1 (wall
 * hover/select on the floor view + camera framing on click). Once focused,
 * this adapter lets the inner LineTool:
 *   - run segmentation continuously on the wall view (the door/window
 *     pipeline ends in `bounding_rectangle` so every confirmed shape is a
 *     rectangle),
 *   - draw new openings via line prompts (defaults to WINDOW; user
 *     converts to door via the outer tool's context menu),
 *   - click-edit an existing door/window AreaAnnotation.
 *
 * Plane handling: `DoorWindowTool` installs the wall plane onto
 * `viewer.viewerMode.associatedPlane` before nesting this adapter. The
 * LineTool's `CoordinateTransformer.screenToWorld` unprojects onto that
 * plane, so prompts + segmentation captures already produce
 * wall-plane-aligned world coordinates. `snapPolygonToBasePlane` removes
 * the residual sub-cm noise by projecting onto the plane in 3D.
 *
 * View handling: the outer tool already framed the wall on click — we
 * mirror its view config so the inner LineTool doesn't try to re-install
 * its own view tabs. `configureViewForShape` / `restoreViewAfterShape`
 * are no-ops for the same reason.
 */
export class DoorWindowEditKindAdapter implements AnnotationKindAdapter {
  /** Opening tools may only edit openings — not rooms/surfaces (also AREA geometry). */
  readonly editDomain = 'door_window' as const;

  readonly polygonPipelineConfigPath: string | null = '/config/segmentation/door-window-poly-pipeline-config.json';

  /** Door/window: eye-space depth pipeline tuned for wall captures. */
  readonly depthPipelineConfigPath: string | null = '/config/segmentation/door-window-depth-pipeline-config.json';

  /**
   * Door/window: 90° snap is the default (shift OPTS OUT). Real-world
   * door/window openings are almost always axis-aligned to their wall
   * direction + vertical; freeform-by-default would make the common
   * case worse, not better.
   */
  readonly invertShiftFor90Snap = true;

  readonly autoCommitNewFromRegion = false;

  /**
   * Openings are rectangles by construction (the polygon pipeline ends in
   * `bounding_rectangle`, and the OPENING_SKETCH encoding is an AABB in wall
   * coordinates) — prompt edits stay perpendicular-only and vertex
   * insert/delete is blocked. See `AnnotationKindAdapter.rectangleOnly`.
   */
  readonly rectangleOnly = true;

  /**
   * The wall the outer DoorWindowTool focused for phase 2: the sketch plus
   * the collinear edge chain the user clicked. Committed rectangles project
   * onto (one of) these edges to become OPENING_SKETCH parameters. Set by
   * the outer tool on wall focus, cleared on focus teardown / floor change.
   */
  private focusContext: { wallSketch: PointoramaWallSketch; edgeIds: string[] } | null = null;

  /** Invoked after a successful OPENING_SKETCH commit — the outer tool wires
   *  its undo snapshot + inner-undo cleanup here. */
  onOpeningSketchCommitted: ((request: OpeningSketchRequest) => void) | null = null;

  /** Invoked after an existing OPENING_SKETCH's elevation rectangle was
   *  edited — the outer tool pushes the geometry-toggle undo snapshot. */
  onOpeningSketchEdited: ((openingId: string, before: OpeningSketchGeometryValues, after: OpeningSketchGeometryValues) => void) | null = null;

  /** The outer tool's elevation overlay — hit-target rectangles hide their
   *  static outline while in prompt-edit. Null-safe. */
  elevationOverlay: OpeningSketchElevationOverlay | null = null;

  setFocusContext(ctx: { wallSketch: PointoramaWallSketch; edgeIds: string[] } | null): void {
    this.focusContext = ctx;
  }

  /**
   * The floor selection context this adapter owns. Door/window lives in the
   * FloorplanPersona, so it tracks the same floor selection rooms do; the
   * adapter reads the floor group id off it for `finishMeasurement`. The
   * opening's own floor Z is derived from the polygon geometry, not here.
   */
  private floorContext: FloorSelectionContext | null = null;

  createSelectionContext(viewer: ViewerType, persona: IPersona): SelectionContextProvider {
    this.floorContext = new FloorSelectionContext(viewer, persona);
    return this.floorContext;
  }

  getViewConfig(): IViewConfig {
    return {
      views: [ViewModes.XRAY_2D, ViewModes.PERSPECTIVE_3D_RGB],
      defaultViewId: ViewModes.XRAY_2D.id,
    };
  }

  /**
   * Hit-test door/window AreaAnnotations under the cursor. The
   * LineTool's idle 3D-click path calls this before falling through to
   * "draw new shape" — if an existing annotation is hit, the mode
   * enters edit mode on it via `enterShapeEditMode(shape)`. Click hits
   * are tested in pixel space against the polygon's projected corners
   * (matches the outer DoorWindowTool's hover hit-tester so the two
   * tools agree on what's "under the cursor").
   */
  hitTestEditableTarget(ctx: HitTestContext): EditableShape | null {
    const { viewer, mouse } = ctx;
    for (const m of viewer.scene.measurements) {
      if (!(m instanceof OpeningAnnotation)) continue;
      if (m.points.length < 3) continue;
      const projected: { x: number; y: number }[] = [];
      let allOnScreen = true;
      for (const p of m.points) {
        const ndc = p.position.clone().project(viewer.scene.getActiveCamera());
        if (ndc.z < -1 || ndc.z > 1) {
          allOnScreen = false;
          break;
        }
        const sx = ((ndc.x + 1) / 2) * viewer.renderer.domElement.clientWidth;
        const sy = ((1 - ndc.y) / 2) * viewer.renderer.domElement.clientHeight;
        projected.push({ x: sx, y: sy });
      }
      if (!allOnScreen) continue;
      if (this.pointInPolygon2D(mouse, projected)) return m as unknown as EditableShape;
    }
    // OPENING_SKETCH openings on the focused wall chain — hit-test their
    // ELEVATION rectangles so the committed (parametric) openings are
    // click-editable in the wall view, same as legacy annotations.
    const focus = this.focusContext;
    if (focus) {
      const picked = pickOpeningSketchRectAtPixel(viewer, focus.wallSketch, focus.edgeIds, mouse, viewer.scene.getActiveCamera());
      if (picked) return new EditableOpeningSketchRect(focus.wallSketch, picked, this.elevationOverlay);
    }
    return null;
  }

  isMouseOverEditableShape(ctx: HitTestContext): boolean {
    return this.hitTestEditableTarget(ctx) !== null;
  }

  /** The MeasureSubTool engine may only edit door/window openings. */
  canEditAnnotation(annotation: EditableAnnotation): boolean {
    return annotation instanceof OpeningAnnotation;
  }

  /** Even-odd point-in-polygon test in pixel space. */
  private pointInPolygon2D(p: { x: number; y: number }, poly: { x: number; y: number }[]): boolean {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i].x, yi = poly[i].y;
      const xj = poly[j].x, yj = poly[j].y;
      if (yi > p.y !== yj > p.y && p.x < ((xj - xi) * (p.y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  }

  shouldRunSegmentationOnEnter(_shape: EditableShape): boolean {
    return true;
  }

  configureViewForShape(_shape: EditableShape, _viewer: Viewer): void {
    // no-op — outer DoorWindowTool already installed the wall focus
  }

  restoreViewAfterShape(_shape: EditableShape, _viewer: Viewer): void {
    // no-op — outer DoorWindowTool tears down the wall focus on
    // disable / floor change / Esc / Enter
  }

  /**
   * Materialize a new opening from a drawn rectangle — as an OPENING_SKETCH
   * on the focused wall's sketch edge, never a legacy OPENING annotation.
   * The polygon (the 4-corner rectangle the door/window polygon pipeline
   * produces, or any hand-drawn shape) is projected onto the focused edge
   * chain via `openingSketchParamsFromPolygon`, which reduces it to its
   * wall-frame AABB — the encoding is structurally a rectangle. Kind is
   * inferred from the rectangle's proportions/position (door vs window).
   *
   * A new opening that SLIGHTLY overlaps an existing OPENING_SKETCH on the
   * same edge is auto-adjusted (slid, and shrunk if the free gap is barely
   * too small) so it lands flush against its neighbour — small brushing /
   * detection inaccuracies shouldn't discard the draw. Only when no
   * placement within `MAX_OPENING_OVERLAP_ADJUST_M` of the drawn interval
   * exists is the draw rejected with a toast (via the
   * `door_window_overlap_rejected` scene event).
   */
  commitNew(viewer: Viewer, polygon: THREE.Vector3[], context: CommitContext): void {
    if (polygon.length < 3) return;
    const focus = this.focusContext;
    if (!focus) {
      console.warn('DoorWindowEditKindAdapter.commitNew: no focused wall sketch — opening not created');
      return;
    }
    const frame = this.getBasePlaneFrame({ viewer, basePlane: context.basePlane, shape: null });
    const floorHeight = this.floorContext?.getFloor()?.height ?? null;
    const inferredKind = inferOpeningKind(polygon, frame, floorHeight);

    const params = openingSketchParamsFromPolygon(
      focus.wallSketch,
      polygon.map((p) => ({ x: p.x, y: p.y, z: p.z })),
      focus.edgeIds,
      /* swingPositive (creation default) */ true,
    );
    if (!params) return;
    const placement = this.resolveOpeningOverlap(viewer, params);
    if (!placement) {
      viewer.scene.dispatchEvent({ type: 'door_window_overlap_rejected' });
      return;
    }

    const groupIdentifier = focus.wallSketch.groupIdentifier ?? this.floorContext?.getGroupIdentifier() ?? null;
    if (!groupIdentifier) return;
    // Mint the id here so the outer tool's undo snapshot re-creates the
    // exact same opening on redo.
    const request: OpeningSketchRequest = {
      identifier: `opening_sketch_${groupIdentifier}_${uuid()}`,
      groupIdentifier,
      wallEdgeId: params.wallEdgeId,
      offset: placement.offset,
      length: placement.length,
      lowerBound: params.lowerBound,
      height: params.height,
      // Not the projection's inset: the drawn polygon lies on the CENTERLINE
      // plane, so its perpendicular offset carries no placement intent.
      // Center the opening in the wall (half thickness) instead.
      inset: defaultOpeningInset(focus.wallSketch, params.wallEdgeId),
      kind: inferredKind,
      doorType: DoorType.SINGLE,
      hingeAtStart: true,
      swingPositive: true,
      rollable: false,
      sourceOpeningId: '',
    };
    viewer.scene.dispatchEvent({ type: 'request_opening_sketch', value: request });
    // Cut the wall hole right away so the commit is visible without waiting
    // for a later interaction to trigger a skin rebuild.
    focus.wallSketch.updateSkinningVisualization();
    this.onOpeningSketchCommitted?.(request);
  }

  /**
   * Along-edge interval of every existing OPENING_SKETCH on the same wall
   * edge whose vertical extent overlaps the new opening's — the intervals a
   * new opening must not intrude into. Chains spanning multiple collinear
   * edges are compared per-edge only — an overlap straddling two edges is
   * not detected (acceptable v1, mirrors the old same-wall AABB test's
   * scope).
   */
  private collectBlockingIntervals(viewer: Viewer, params: OpeningSketchGeometryParams): Array<{ lo: number; hi: number }> {
    const EPS = 1e-6;
    const vLo = params.lowerBound;
    const vHi = params.lowerBound + params.height;
    const blockers: Array<{ lo: number; hi: number }> = [];
    for (const os of viewer.scene.openingSketches) {
      try {
        if ((getOpeningSketchWallEdgeIdProperty(os.identifier).value || '') !== params.wallEdgeId) continue;
        const oLo = getOpeningSketchOffsetProperty(os.identifier).value ?? 0;
        const oHi = oLo + (getOpeningSketchLengthProperty(os.identifier).value ?? 0);
        const ovLo = getOpeningSketchLowerBoundProperty(os.identifier).value ?? 0;
        const ovHi = ovLo + (getOpeningSketchHeightProperty(os.identifier).value ?? 0);
        if (vLo < ovHi - EPS && ovLo < vHi - EPS) blockers.push({ lo: oLo, hi: oHi });
      } catch {
        // Property service not ready for this opening — skip it.
      }
    }
    return blockers;
  }

  /**
   * Fit the new opening's along-edge interval around the existing openings on
   * the same edge. A draw that doesn't overlap any existing opening passes
   * through unchanged (touching is fine — side-by-side windows). A slight
   * overlap is resolved by sliding the opening into the nearest free gap —
   * flush against its neighbour — shrinking it only when the gap is barely
   * too small. Only existing openings constrain the placement: a drawn
   * interval was never required to stay inside its projected edge's [0, len]
   * domain (rects spanning a collinear chain project past the edge ends all
   * the time), so the gaps here are unbounded on the outside too. Returns
   * null when no placement within `MAX_OPENING_OVERLAP_ADJUST_M` of the
   * drawn interval exists.
   */
  private resolveOpeningOverlap(viewer: Viewer, params: OpeningSketchGeometryParams): { offset: number; length: number } | null {
    const EPS = 1e-6;
    const lo = params.offset;
    const hi = params.offset + params.length;
    const blockers = this.collectBlockingIntervals(viewer, params).sort((p, q) => p.lo - q.lo);
    if (!blockers.some((b) => lo < b.hi - EPS && b.lo < hi - EPS)) {
      return { offset: params.offset, length: params.length };
    }

    const merged: Array<{ lo: number; hi: number }> = [];
    for (const blocker of blockers) {
      const last = merged[merged.length - 1];
      if (last && blocker.lo <= last.hi + EPS) last.hi = Math.max(last.hi, blocker.hi);
      else merged.push({ ...blocker });
    }
    const gaps: Array<{ lo: number; hi: number }> = [{ lo: Number.NEGATIVE_INFINITY, hi: merged[0].lo }];
    for (let i = 0; i < merged.length; i++) {
      gaps.push({ lo: merged[i].hi, hi: i + 1 < merged.length ? merged[i + 1].lo : Number.POSITIVE_INFINITY });
    }

    // Best placement across the gaps: keep the drawn length when the gap
    // allows, else fill the gap; smallest total adjustment (slide + shrink)
    // wins.
    let best: { offset: number; length: number; cost: number } | null = null;
    for (const gap of gaps) {
      const length = Math.min(params.length, gap.hi - gap.lo);
      if (length < MIN_ADJUSTED_OPENING_LENGTH_M) continue;
      const offset = THREE.MathUtils.clamp(lo, gap.lo, gap.hi - length);
      const cost = Math.abs(offset - lo) + (params.length - length);
      if (!best || cost < best.cost) best = { offset, length, cost };
    }
    if (!best || best.cost > MAX_OPENING_OVERLAP_ADJUST_M) return null;
    return { offset: best.offset, length: best.length };
  }

  /**
   * Persist the polygon edit on an existing AreaAnnotation. The shape
   * is the AreaAnnotation passed into `mode.enterShapeEditMode(shape)`;
   * its identifier is the annotation's stable id, which the React
   * `door_window_updated` handler uses to write the updated points
   * into the reactive property store.
   */
  commitEdit(viewer: Viewer, shape: EditableShape, polygon: THREE.Vector3[]): void {
    // OPENING_SKETCH elevation rectangle: map the edited polygon back onto
    // the sketch's parametric encoding instead of the legacy polygon write.
    if (shape instanceof EditableOpeningSketchRect) {
      this.commitOpeningSketchEdit(shape, polygon);
      return;
    }
    const id = (shape as unknown as { identifier?: string }).identifier;
    if (!id) return;
    // Apply the polygon to the live opening before dispatching: on undo/redo
    // restore the caller passes the restored polygon (the annotation hasn't
    // been mutated yet), so without this the geometry persists but the in-scene
    // visual doesn't move. For live edits this re-applies the current polygon
    // (harmless). setPoints rebuilds the annotation's spheres/edges.
    if (polygon.length >= 3 && shape instanceof OpeningAnnotation) {
      shape.setPoints(polygon.map((p) => ({ position: p.clone(), pointCloudId: undefined })));
    }
    // The opening's wall midpoint may have moved into a different room pair;
    // re-resolve and write the updated connectedRooms before dispatching the
    // update. Floor Z is the polygon's minimum Z — the opening rectangle's
    // bottom edge sits on the floor by construction.
    const floorZ = polygon.reduce((min, p) => (p.z < min ? p.z : min), Infinity);
    if (Number.isFinite(floorZ)) {
      writeConnectedRoomsForOpening(viewer, id, polygon, floorZ);
    }
    viewer.scene.dispatchEvent({
      type: 'door_window_updated',
      annotationId: id,
      polygon: polygon.map((p) => ({ x: p.x, y: p.y, z: p.z })),
    });
  }

  /**
   * Persist an edited elevation rectangle onto its OPENING_SKETCH: project
   * the polygon back to (edgeId, offset, length, sill, height) via the shared
   * mapping — preferring the focused chain — and write the properties. The
   * stored `inset` is deliberately preserved: the edited polygon lies on the
   * wall CENTERLINE plane, so its perpendicular offset carries no inset
   * information. Re-cuts the wall hole and reports before/after for undo.
   */
  private commitOpeningSketchEdit(shape: EditableOpeningSketchRect, polygon: THREE.Vector3[]): void {
    const focus = this.focusContext;
    if (!focus || polygon.length < 3) return;
    const before = readOpeningSketchGeometry(shape.identifier);
    if (!before) return;
    const params = openingSketchParamsFromPolygon(
      focus.wallSketch,
      polygon.map((p) => ({ x: p.x, y: p.y, z: p.z })),
      focus.edgeIds,
      /* swingPositive — only affects the discarded inset */ true,
    );
    if (!params) return;
    const after: OpeningSketchGeometryValues = {
      wallEdgeId: params.wallEdgeId,
      offset: params.offset,
      length: params.length,
      lowerBound: params.lowerBound,
      height: params.height,
    };
    writeOpeningSketchGeometry(shape.identifier, after);
    focus.wallSketch.updateSkinningVisualization();
    this.onOpeningSketchEdited?.(shape.identifier, before, after);
  }

  /**
   * Project each vertex onto the wall plane that `DoorWindowTool`
   * installed on `viewer.viewerMode.associatedPlane`. The plane has a
   * world-space origin (the wall midpoint) and a horizontal normal —
   * projection collapses the small perpendicular noise the segmentation
   * + manual-prompt pipeline introduces. When no plane is installed
   * (defensive — phase-2 shouldn't run without wall focus), vertices
   * pass through unchanged.
   */
  snapPolygonToBasePlane(vertices: THREE.Vector3[], context: { viewer: Viewer; basePlane: BasePlaneState | null; shape: EditableShape | null }): THREE.Vector3[] {
    const plane = context.viewer.viewerMode.associatedPlane;
    if (!plane) return vertices;
    const origin = plane.position;
    const normal = plane.getNormal().clone().normalize();
    return vertices.map((v) => {
      const offset = v.clone().sub(origin);
      const perp = offset.dot(normal);
      return v.clone().addScaledVector(normal, -perp);
    });
  }

  /**
   * Vertical frame on the focused wall plane. Local axes: u along the
   * wall direction (horizontal in world), v vertical (world +Z), normal
   * = wall-perpendicular (horizontal). With this frame installed, the
   * XY-hardcoded snap modules see the wall as their "floor" and run
   * the same math they always did. Without it (no associatedPlane —
   * shouldn't happen in phase-2, but defensive) the modules fall back
   * to world XY which would flatten everything to floor.
   */
  getBasePlaneFrame(context: { viewer: Viewer; basePlane: BasePlaneState | null; shape: EditableShape | null }): { origin: THREE.Vector3; u: THREE.Vector3; v: THREE.Vector3; normal: THREE.Vector3 } | null {
    const plane = context.viewer.viewerMode.associatedPlane;
    if (!plane) return null;
    const normal = plane.getNormal().clone().normalize();
    // Wall direction in XY is perpendicular to the wall normal in XY.
    // Pick u = normalize(cross(normal, world +Z)) — that's horizontal,
    // in-plane, and orthogonal to both. v = cross(normal, u) gives a
    // right-handed frame; for a horizontal-normal wall that comes out
    // vertical (≈ world +Z), which is what we want.
    const worldZ = new THREE.Vector3(0, 0, 1);
    const u = new THREE.Vector3().crossVectors(normal, worldZ).normalize();
    // Degenerate (normal is parallel to +Z, i.e. a horizontal "wall")
    // — fall back to world +X. Shouldn't happen for real walls but
    // keeps the math defined.
    if (u.lengthSq() < 1e-9) u.set(1, 0, 0);
    const v = new THREE.Vector3().crossVectors(normal, u).normalize();
    return { origin: plane.position.clone(), u, v, normal };
  }

  /**
   * Build the 8 corners of an oriented box around the focused wall
   * slab, sized to the wall focus's clip BoxVolume in spirit: along the
   * wall (u), vertical (v), and a thin slab along the normal (n) just
   * wide enough to cover wall thickness + a small margin. The screenshot
   * encoder projects these 8 corners through the view matrix to derive
   * a tight eye-Z range — without this, the encoder uses a world-Z slab
   * combined with the cloud's X/Y bbox, which on a wall-perpendicular
   * camera produces a hugely over-wide eye-Z range and the B channel
   * collapses to a narrow gray band (segmentation can't distinguish
   * front-of-wall from back-of-wall content).
   *
   * Dimensions are heuristic: the wall plane's `radius` field (set by
   * `PointoramaPlane.radius`, defaults to 1m) covers half-extents along
   * the in-plane axes; we double it for the box extent. Along the
   * normal we use a 0.5m thickness — typical wall + margin. Refine if
   * needed.
   */
  getCaptureBoxCorners(context: { viewer: Viewer; basePlane: BasePlaneState | null; shape: EditableShape | null }): THREE.Vector3[] | null {
    const frame = this.getBasePlaneFrame(context);
    if (!frame) return null;
    const plane = context.viewer.viewerMode.associatedPlane;
    // `DoorWindowTool.installWallPlane` stashes the wall's in-plane
    // half-extents on the plane so we can derive an exact oriented
    // bounding box for the capture without rebuilding the wall geometry.
    // Falls back to a generous 5m × 5m × 0.5m slab if the marker isn't
    // present (defensive — shouldn't happen in phase-2).
    const stash = (plane as unknown as { __wallHalfExtents?: { u: number; v: number; n: number } }).__wallHalfExtents;
    const halfU = stash?.u ?? 5;
    const halfV = stash?.v ?? 5;
    const halfN = stash?.n ?? 0.5;
    const corners: THREE.Vector3[] = [];
    for (const su of [-1, 1]) {
      for (const sv of [-1, 1]) {
        for (const sn of [-1, 1]) {
          const c = frame.origin.clone()
            .addScaledVector(frame.u, su * halfU)
            .addScaledVector(frame.v, sv * halfV)
            .addScaledVector(frame.normal, sn * halfN);
          corners.push(c);
        }
      }
    }
    return corners;
  }
}

const RAY_EPS = 1e-9;
/**
 * Per-direction cap on the connected-rooms raycast. An opening's adjacent
 * rooms always sit immediately on either side of its wall — a thicker
 * exterior wall is still <1m in any sane building — so capping at 1m
 * prevents the ray from skipping over an empty courtyard or wall gap and
 * grabbing a wholly unrelated room behind it.
 */
const RAY_MAX_DISTANCE_M = 1;

/**
 * Resolve the two rooms an opening connects by raycasting from the opening's
 * along-wall midpoint in the perpendicular direction (both +/-). The first
 * room polygon hit in each direction wins; either side can be null (exterior
 * walls, openings between a room and outside).
 *
 * Geometry: the opening rectangle's *longest* edge is along the wall. We
 * take its midpoint at floor Z and the perpendicular direction in 2D, then
 * cast against every room polygon's edges. The first polygon whose any edge
 * the ray crosses (smallest t) is the adjacent room on that side. The ray
 * could also "enter" a polygon by being inside it at the origin (the opening
 * midpoint sits *in* the wall gap, which is never inside a room — rooms are
 * inset from the wall centerline — but defensively we also check
 * point-in-polygon for the origin).
 *
 * The raycast is purely 2D, but room polygons across storeys overlap in XY
 * (floors stack), so we first restrict the candidate rooms to those whose
 * vertical extent contains `floorZ` — otherwise an opening would link to a
 * room directly above or below it on another floor.
 */
function resolveConnectedRooms(
  rooms: PointoramaRoom[],
  polygon: THREE.Vector3[],
  floorZ: number,
): { roomAId: string | null; roomBId: string | null } {
  const axis = findLongestAxis(polygon);
  if (!axis) return { roomAId: null, roomBId: null };
  const { midX, midY, perpX, perpY } = axis;

  // Keep only rooms on the opening's own floor. Degrade to all rooms when
  // nothing matches (missing/bogus floorZ) rather than resolving zero links.
  const onFloor = roomsOnFloor(rooms, floorZ);
  const candidates = onFloor.length > 0 ? onFloor : rooms;

  const sideA = firstRoomHit(candidates, midX, midY, perpX, perpY);
  const sideB = firstRoomHit(candidates, midX, midY, -perpX, -perpY);
  return { roomAId: sideA, roomBId: sideB };
}

/** Vertical slack (m) letting a door bottom sit slightly below its room's
 *  floor plane when matching an opening to the rooms on its floor. */
const FLOOR_Z_TOLERANCE_M = 0.1;

/**
 * Keep only rooms whose [base, ceiling] vertical span contains `floorZ`
 * (the opening's bottom elevation). Filters out same-XY-footprint rooms on
 * other storeys so an opening links only to rooms on its own floor. Returns
 * an empty array for a non-finite `floorZ` so the caller can degrade.
 */
function roomsOnFloor(rooms: PointoramaRoom[], floorZ: number): PointoramaRoom[] {
  if (!Number.isFinite(floorZ)) return [];
  return rooms.filter((room) => {
    const baseZ = room.getBase().position.z;
    const ceilingZ = room.getCeiling().position.z;
    const lo = Math.min(baseZ, ceilingZ) - FLOOR_Z_TOLERANCE_M;
    const hi = Math.max(baseZ, ceilingZ) + FLOOR_Z_TOLERANCE_M;
    return floorZ >= lo && floorZ <= hi;
  });
}

/**
 * Find the polygon's longest edge in XY and return its midpoint + the
 * perpendicular direction (unit vector in XY). For a door/window rectangle
 * one pair of opposite edges sits horizontal-along-the-wall and the other
 * pair vertical — the horizontal pair is the longest by a wide margin in
 * most cases. We pick the single longest edge directly rather than the
 * "horizontal pair" because (a) for a square-ish opening they're almost
 * equal and (b) the perpendicular of either pair gives the same axis.
 */
function findLongestAxis(polygon: THREE.Vector3[]): { midX: number; midY: number; perpX: number; perpY: number } | null {
  let bestLen2 = 0;
  let bestI = -1;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    if (len2 > bestLen2) {
      bestLen2 = len2;
      bestI = i;
    }
  }
  if (bestI < 0 || bestLen2 < RAY_EPS) return null;
  const a = polygon[bestI];
  const b = polygon[(bestI + 1) % polygon.length];
  const len = Math.sqrt(bestLen2);
  const ux = (b.x - a.x) / len;
  const uy = (b.y - a.y) / len;
  return {
    midX: (a.x + b.x) / 2,
    midY: (a.y + b.y) / 2,
    perpX: -uy,
    perpY: ux,
  };
}

/**
 * Cast a 2D ray from `(ox, oy)` in direction `(dx, dy)` against every room
 * polygon. Returns the room identifier whose first edge-crossing has the
 * smallest t > 0, or null if no room is hit within `RAY_MAX_DISTANCE_M`.
 *
 * For each polygon: also bail with that room if the origin is *inside* it
 * — the opening's midpoint should always be in the wall gap (rooms inset
 * from wall centerline by half wall thickness), but a tiny imprecision or
 * an opening drawn on a polygon-interior would still resolve correctly.
 */
function firstRoomHit(rooms: PointoramaRoom[], ox: number, oy: number, dx: number, dy: number): string | null {
  let bestRoomId: string | null = null;
  let bestT = RAY_MAX_DISTANCE_M;
  for (const room of rooms) {
    const poly = room.getPolygon();
    if (poly.length < 3) continue;
    if (pointInPolygonXY(ox, oy, poly)) {
      // Origin inside this room — distance zero, can't be beaten.
      return room.identifier;
    }
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i];
      const b = poly[(i + 1) % poly.length];
      const t = raySegmentT(ox, oy, dx, dy, a.x, a.y, b.x, b.y);
      if (t === null) continue;
      if (t < bestT) {
        bestT = t;
        bestRoomId = room.identifier;
      }
    }
  }
  return bestRoomId;
}

/**
 * Standard 2D ray-vs-segment intersection. Returns t along the ray (≥ 0)
 * where the ray hits segment `(ax, ay) → (bx, by)`, or null when there is
 * no forward-direction hit on the segment proper (u outside [0, 1]).
 */
function raySegmentT(ox: number, oy: number, dx: number, dy: number, ax: number, ay: number, bx: number, by: number): number | null {
  const sx = bx - ax;
  const sy = by - ay;
  const det = dx * sy - dy * sx;
  if (Math.abs(det) < RAY_EPS) return null;
  const ex = ax - ox;
  const ey = ay - oy;
  const t = (ex * sy - ey * sx) / det;
  const u = (ex * dy - ey * dx) / det;
  if (t < 0) return null;
  if (u < 0 || u > 1) return null;
  return t;
}

/**
 * Resolve adjacent rooms for the given opening polygon and persist them to
 * the opening's `opening_room_ids` reactive property (registered in
 * `@pointorama/potree-shared`). Stored as a `string[]` of 0–2 room
 * identifiers — exterior-facing sides drop out, so a window into a single
 * room becomes a 1-element array and a door between two rooms a 2-element.
 */
function writeConnectedRoomsForOpening(
  viewer: Viewer,
  openingId: string,
  polygon: THREE.Vector3[],
  floorZ: number,
): void {
  const rooms = viewer.scene.rooms as PointoramaRoom[];
  if (rooms.length === 0) return;
  const { roomAId, roomBId } = resolveConnectedRooms(rooms, polygon, floorZ);
  const ids: string[] = [];
  if (roomAId) ids.push(roomAId);
  if (roomBId && roomBId !== roomAId) ids.push(roomBId);
  try {
    getOpeningRoomIdsProperty(openingId).value = ids;
  } catch {
    // Global property service not initialised yet (e.g. tool fired before
    // the persona system bootstrapped reactive properties). The opening is
    // still created; the missing link can be backfilled by re-saving the
    // opening, since this same function runs on every commitEdit.
  }
}

/** 2D AABB in the (u, v) base-plane frame. */
interface PlaneAabb {
  uMin: number;
  uMax: number;
  vMin: number;
  vMax: number;
}

/**
 * Project a polygon's vertices into a base-plane (u, v) frame and return
 * the axis-aligned bounding box. Returns null for empty input.
 */
function polygonAabbInFrame(polygon: THREE.Vector3[], frame: { origin: THREE.Vector3; u: THREE.Vector3; v: THREE.Vector3 }): PlaneAabb | null {
  if (polygon.length === 0) return null;
  let uMin = Infinity;
  let uMax = -Infinity;
  let vMin = Infinity;
  let vMax = -Infinity;
  for (const p of polygon) {
    const offset = p.clone().sub(frame.origin);
    const u = offset.dot(frame.u);
    const v = offset.dot(frame.v);
    if (u < uMin) uMin = u;
    if (u > uMax) uMax = u;
    if (v < vMin) vMin = v;
    if (v > vMax) vMax = v;
  }
  return { uMin, uMax, vMin, vMax };
}

/**
 * Heuristic kind inference for a freshly-drawn opening. Score-based:
 * each signal independently votes door vs window; the higher score wins,
 * ties fall to WINDOW (preserves the historical default — and a manual
 * conversion via the context menu is one click). Polygons are arbitrary
 * (rectangles by default but the user can draw any shape), so all
 * measurements come from the (u, v) AABB on the wall plane plus the
 * polygon's world-Z extent — that's invariant under shape detail and
 * works for both rectangles and freeform outlines.
 *
 * Signals, in order of how discriminative they are in practice:
 *   1. Bottom-to-floor gap (×2 weight) — doors touch the floor (< 0.15 m);
 *      windows have a sill (> 0.3 m). Strongest single signal.
 *   2. Aspect ratio — doors are tall+narrow (h/w > 1.3); windows are
 *      square-ish or wider than tall (h/w < 1.0).
 *   3. Width on the wall — doors 0.7–1.2 m; windows 0.5–2.5 m.
 *   4. Height — doors 1.8–2.3 m; windows 0.6–1.7 m.
 *
 * Inputs the caller may not have: `frame` is null when no wall plane is
 * installed (defensive — phase-2 shouldn't run without one); `floorZ` is
 * null when the floor context can't resolve a selected floor. In either
 * case we degrade gracefully — signals that need the missing input are
 * skipped, and the remaining signals still vote.
 */
function inferOpeningKind(
  polygon: THREE.Vector3[],
  frame: { origin: THREE.Vector3; u: THREE.Vector3; v: THREE.Vector3 } | null,
  floorZ: number | null,
): OpeningKind {
  let doorScore = 0;
  let windowScore = 0;

  const aabb = frame ? polygonAabbInFrame(polygon, frame) : null;
  // Width: along the wall (u). Height: vertical (v); the wall frame's v
  // axis is world-vertical for upright walls, so this equals the world-Z
  // extent in practice. We use the frame measurement when available so
  // skewed wall frames stay consistent.
  const width = aabb ? aabb.uMax - aabb.uMin : null;
  const height = aabb ? aabb.vMax - aabb.vMin : null;

  // Bottom of polygon in world Z. Always available — independent of the
  // wall frame.
  let polygonMinZ = Infinity;
  for (const p of polygon) {
    if (p.z < polygonMinZ) polygonMinZ = p.z;
  }

  // 1. Bottom-to-floor gap (weighted ×2).
  if (floorZ !== null && Number.isFinite(polygonMinZ)) {
    const gap = polygonMinZ - floorZ;
    if (gap < 0.15) doorScore += 2;
    else if (gap > 0.3) windowScore += 2;
  }

  // 2. Aspect ratio.
  if (width !== null && height !== null && width > 0.01) {
    const ratio = height / width;
    if (ratio > 1.3) doorScore += 1;
    else if (ratio < 1.0) windowScore += 1;
  }

  // 3. Width on the wall (±0.25 m bands around the typical extremes,
  // matching the spec).
  if (width !== null) {
    if (width >= 0.7 && width <= 1.2) doorScore += 1;
    else if (width >= 0.5 && width <= 2.5 && (width < 0.7 || width > 1.2)) windowScore += 1;
  }

  // 4. Height (±0.25 m around door/window typical heights).
  if (height !== null) {
    if (height >= 1.8 && height <= 2.3) doorScore += 1;
    else if (height >= 0.6 && height <= 1.7) windowScore += 1;
  }

  return doorScore > windowScore ? OpeningKind.DOOR : OpeningKind.WINDOW;
}
